import { c as _c } from "react/compiler-runtime";
import { Button, IconButton } from '@repo/design-system/components/ds/button';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { ScrollableContent } from '@repo/design-system/components/layout/scrollable-content';
import { TextInput } from '@repo/design-system/components/ui/text-input';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { getFontInfoForGlyph, getIconForGlyph, getSubcategoryIconStyle } from '@repo/types/maps/map-utils';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { LocationContentEditing } from './location-content-editing';
interface CategoryIconProps {
  categoryColor: string;
  subcategoryIcon: string;
  onClick: () => void;
}
const categoryIconStyles = css.raw({
  borderRadius: 'full',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 'sm',
  color: 'white',
  boxShadow: 'sm'
});
const CategoryIconButton: React.FC<CategoryIconProps> = t0 => {
  const $ = _c(17);
  const {
    categoryColor,
    subcategoryIcon,
    onClick
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["game_map"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useT(t1);
  let iconChar;
  let t2;
  if ($[1] !== subcategoryIcon) {
    const fontInfo = getFontInfoForGlyph(subcategoryIcon);
    iconChar = getIconForGlyph(subcategoryIcon);
    t2 = getSubcategoryIconStyle(fontInfo.fontName);
    $[1] = subcategoryIcon;
    $[2] = iconChar;
    $[3] = t2;
  } else {
    iconChar = $[2];
    t2 = $[3];
  }
  const iconStyle = t2;
  let t3;
  if ($[4] !== categoryColor || $[5] !== iconStyle) {
    t3 = {
      backgroundColor: categoryColor,
      ...iconStyle
    };
    $[4] = categoryColor;
    $[5] = iconStyle;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== onClick) {
    t4 = () => onClick();
    $[7] = onClick;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t(_temp);
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = css(categoryIconStyles);
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== iconChar || $[13] !== t3 || $[14] !== t4 || $[15] !== t5) {
    t7 = <IconButton variant="ghost" style={t3} onClick={t4} color="primary" aria-label={t5} size="sm" className={t6}>{iconChar}</IconButton>;
    $[12] = iconChar;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};
interface LocationInfoEditingProps {
  title: string;
  description: string;
  color: string;
  imageUrl?: string | null;
  videoUrl?: string | null;
  categoryColor: string;
  subcategoryIcon: string;
  onTitleChange: (title: string) => void;
  onDescriptionChange: (description: string) => void;
  onColorChange: (color: string) => void;
  onSave: () => void;
  onCancel: () => void;
  onPanToLocation: () => void;
  isSaveDisabled?: boolean;
  isEditingNewlyCreatedPin?: boolean;
}
export const LocationInfoEditing: React.FC<LocationInfoEditingProps> = t0 => {
  const $ = _c(49);
  const {
    title,
    description,
    color,
    categoryColor,
    subcategoryIcon,
    onTitleChange,
    onDescriptionChange,
    onColorChange,
    onSave,
    onCancel,
    onPanToLocation,
    isSaveDisabled: t1,
    isEditingNewlyCreatedPin: t2
  } = t0;
  const isSaveDisabled = t1 === undefined ? false : t1;
  const isEditingNewlyCreatedPin = t2 === undefined ? false : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["game_map"];
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const {
    t
  } = useT(t3);
  const titleInputRef = useRef(null);
  let t4;
  let t5;
  if ($[1] !== isEditingNewlyCreatedPin) {
    t4 = () => {
      if (isEditingNewlyCreatedPin && titleInputRef.current) {
        titleInputRef.current.focus();
        titleInputRef.current.select();
      }
    };
    t5 = [isEditingNewlyCreatedPin];
    $[1] = isEditingNewlyCreatedPin;
    $[2] = t4;
    $[3] = t5;
  } else {
    t4 = $[2];
    t5 = $[3];
  }
  useEffect(t4, t5);
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = css.raw({
      padding: "4",
      paddingBottom: "12"
    });
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== categoryColor || $[6] !== onPanToLocation || $[7] !== subcategoryIcon) {
    t7 = <CategoryIconButton categoryColor={categoryColor} subcategoryIcon={subcategoryIcon} onClick={onPanToLocation} />;
    $[5] = categoryColor;
    $[6] = onPanToLocation;
    $[7] = subcategoryIcon;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      flex: 1
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== onTitleChange) {
    t9 = e => onTitleChange(e.target.value);
    $[10] = onTitleChange;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t) {
    t10 = t(_temp2);
    $[12] = t;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10 || $[15] !== t9 || $[16] !== title) {
    t11 = <div style={t8}><TextInput ref={titleInputRef} value={title} onChange={t9} placeholder={t10} size="sm" fullWidth={true} /></div>;
    $[14] = t10;
    $[15] = t9;
    $[16] = title;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11 || $[19] !== t7) {
    t12 = <FlexRow align="center" justify="between" gap="8" width="full" className={t6}>{t7}{t11}</FlexRow>;
    $[18] = t11;
    $[19] = t7;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = css({
      overflow: "hidden",
      display: "flex",
      flex: "1"
    });
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = css.raw({
      padding: "8"
    });
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== color || $[24] !== description || $[25] !== onColorChange || $[26] !== onDescriptionChange) {
    t15 = <ScrollableContent maxHeight={600} className={t13}><FlexColumn gap="12"><FlexColumn width="full" className={t14}><LocationContentEditing description={description} onDescriptionChange={onDescriptionChange} onColorChange={onColorChange} color={color} /></FlexColumn></FlexColumn></ScrollableContent>;
    $[23] = color;
    $[24] = description;
    $[25] = onColorChange;
    $[26] = onDescriptionChange;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = css({
      flex: "1"
    });
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t) {
    t17 = t(_temp3);
    $[29] = t;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== onCancel || $[32] !== t17) {
    t18 = <Button onClick={onCancel} color="secondary" size="lg" className={t16}>{t17}</Button>;
    $[31] = onCancel;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = css({
      flex: "1"
    });
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== isEditingNewlyCreatedPin || $[36] !== t) {
    t20 = isEditingNewlyCreatedPin ? t(_temp4) : t(_temp5);
    $[35] = isEditingNewlyCreatedPin;
    $[36] = t;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== isSaveDisabled || $[39] !== onSave || $[40] !== t20) {
    t21 = <Button onClick={onSave} color="white" size="lg" disabled={isSaveDisabled} className={t19}>{t20}</Button>;
    $[38] = isSaveDisabled;
    $[39] = onSave;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== t18 || $[43] !== t21) {
    t22 = <FlexRow gap="8" justify="end" width="full">{t18}{t21}</FlexRow>;
    $[42] = t18;
    $[43] = t21;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== t12 || $[46] !== t15 || $[47] !== t22) {
    t23 = <>{t12}{t15}{t22}</>;
    $[45] = t12;
    $[46] = t15;
    $[47] = t22;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  return t23;
};
function _temp($) {
  return $.show_on_map;
}
function _temp2($) {
  return $.location_info.enter_pin_title_placeholder;
}
function _temp3($_0) {
  return $_0.cancel;
}
function _temp4($_1) {
  return $_1.add_to_map;
}
function _temp5($_2) {
  return $_2.location_info.save_short;
}